import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CALENDAR_LOCALE } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import type { CalendarTask } from "./types";
import CalendarHeader from "./CalendarHeader";
import CalendarMonthGrid from "./CalendarMonthGrid";
import CalendarWeekGrid from "./CalendarWeekGrid";
import CalendarDayView from "./CalendarDayView";
import type { CalendarViewMode } from "./CalendarHeader";

export type { CalendarTask } from "./types";
export type { CalendarViewMode } from "./CalendarHeader";

const MONTHS_GENITIVE: Record<Locale, string[]> = {
  ru: [
    "января", "февраля", "марта", "апреля", "мая", "июня",
    "июля", "августа", "сентября", "октября", "ноября", "декабря",
  ],
  kk: [
    "қаңтардың", "ақпанның", "наурыздың", "сәуірдің", "мамырдың", "маусымның",
    "шілденің", "тамыздың", "қыркүйектің", "қазанның", "қарашаның", "желтоқсанның",
  ],
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ],
};

function toDateKeyFromString(dueDate: string): string | null {
  if (dueDate.includes(".")) {
    const parts = dueDate.split(".");
    if (parts.length < 3) return null;
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  const match = dueDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

interface Props {
  tasks: CalendarTask[];
  locale: Locale;
  onTaskClick: (task: CalendarTask) => void;
  title?: string;
}

export default function TaskCalendar({ tasks, locale, onTaskClick, title }: Props) {
  const { t } = useTranslation("Common");
  const [viewMode, setViewMode] = useState<CalendarViewMode>("month");
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  const tasksByDate = useMemo(() => {
    const map = new Map<string, CalendarTask[]>();
    for (const task of tasks) {
      if (task.task_type === "kpi" || !task.due_date) continue;
      const key = toDateKeyFromString(task.due_date);
      if (!key) continue;
      const arr = map.get(key);
      if (arr) arr.push(task);
      else map.set(key, [task]);
    }
    return map;
  }, [tasks]);

  const headerTitle = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (viewMode === "month") {
      const monthName = CALENDAR_LOCALE[locale].months[month];
      return `${monthName} ${year}`;
    }

    if (viewMode === "day") {
      const day = currentDate.getDate();
      const monthGenitive = MONTHS_GENITIVE[locale][month];
      return `${day} ${monthGenitive} ${year}`;
    }

    const dayOfWeek = (currentDate.getDay() + 6) % 7;
    const monday = new Date(year, month, currentDate.getDate() - dayOfWeek);
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);

    const monDay = monday.getDate();
    const sunDay = sunday.getDate();
    const monMonth = MONTHS_GENITIVE[locale][monday.getMonth()];
    const sunMonth = MONTHS_GENITIVE[locale][sunday.getMonth()];
    const monYear = monday.getFullYear();
    const sunYear = sunday.getFullYear();

    if (monYear !== sunYear) {
      return `${monDay} ${monMonth} ${monYear} – ${sunDay} ${sunMonth} ${sunYear}`;
    }
    if (monday.getMonth() !== sunday.getMonth()) {
      return `${monDay} ${monMonth} – ${sunDay} ${sunMonth} ${year}`;
    }
    return `${monDay} – ${sunDay} ${sunMonth} ${year}`;
  }, [currentDate, viewMode, locale]);

  function handlePrev() {
    setCurrentDate((prev) => {
      if (viewMode === "month") return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      if (viewMode === "week") return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 7);
      return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1);
    });
  }

  function handleNext() {
    setCurrentDate((prev) => {
      if (viewMode === "month") return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      if (viewMode === "week") return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 7);
      return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1);
    });
  }

  function handleToday() {
    setCurrentDate(new Date());
  }

  return (
    <div className="p-7 radius-3xl surface-base-fill">
      <h3 className="text-body-bold-lg content-base-primary mb-5">{title ?? t("calendar.title")}</h3>

      <CalendarHeader
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        title={headerTitle}
      />

      <div className="flex flex-col min-h-[40rem]">
        {viewMode === "month" && (
          <CalendarMonthGrid
            currentDate={currentDate}
            tasksByDate={tasksByDate}
            locale={locale}
            onTaskClick={onTaskClick}
          />
        )}
        {viewMode === "week" && (
          <CalendarWeekGrid
            currentDate={currentDate}
            tasksByDate={tasksByDate}
            locale={locale}
            onTaskClick={onTaskClick}
          />
        )}
        {viewMode === "day" && (
          <CalendarDayView currentDate={currentDate} tasksByDate={tasksByDate} onTaskClick={onTaskClick} />
        )}
      </div>
    </div>
  );
}
